import { useCallback, useEffect, useState } from "react";
import api, { money } from "../lib/api";
import { errorToastFromAxios } from "../lib/supportToast";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Gauge, Clock, CheckCircle2, XCircle } from "lucide-react";

/**
 * LimitRequestCard — today's send limit, and how to ask for more.
 *
 * Every business starts on a daily limit set with our partner bank. A member
 * who needs to move more asks here; a Verqo admin decides, with their
 * connected bank, trading history and verification state in front of them.
 */
export default function LimitRequestCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ requested: "", volume: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData((await api.get("/shield/limit-request")).data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const requested = Number(form.requested);
    if (!requested || requested <= 0) return toast.error("Enter the daily limit you need.");
    if (form.reason.trim().length < 10) {
      return toast.error("Tell us briefly why — it speeds up the decision.");
    }
    setBusy(true);
    try {
      const r = await api.post("/shield/limit-request", {
        requested_daily_limit: requested,
        expected_monthly_volume: form.volume ? Number(form.volume) : undefined,
        reason: form.reason.trim(),
      });
      toast.success(r.data?.message || "Request received.");
      setOpen(false);
      setForm({ requested: "", volume: "", reason: "" });
      load();
    } catch (e) {
      errorToastFromAxios(e, "We could not send that request.");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const latest = data.latest_request;
  const pending = latest?.status === "pending";

  return (
    <div className="rounded-2xl border border-border bg-white p-6" data-testid="limit-request-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Gauge className="mt-0.5 h-5 w-5 text-foreground/45" />
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">
              Daily send limit
            </div>
            <div className="display mt-1 text-xl tracking-tight" data-testid="limit-current">
              {money(data.current_daily_limit_cents / 100)} a day
            </div>
            <p className="mt-1 text-sm text-foreground/55">
              Wires are capped at {money(data.wire_max_cents / 100)} each. Need more? Ask and
              we will review it, usually within a business day.
            </p>
          </div>
        </div>
        <Button variant="outline" className="h-10 rounded-full"
                disabled={!data.can_request} onClick={() => setOpen(true)}
                data-testid="limit-request-btn">
          {pending ? "Request under review" : "Request a higher limit"}
        </Button>
      </div>

      {latest && (
        <div className="mt-4 flex items-start gap-2 border-t border-border pt-4 text-sm"
             data-testid="limit-request-status">
          {pending && <Clock className="mt-0.5 h-4 w-4 text-amber-600" />}
          {latest.status === "approved" && <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />}
          {latest.status === "declined" && <XCircle className="mt-0.5 h-4 w-4 text-red-500" />}
          <div className="text-foreground/65">
            {pending && (
              <>You asked for {money(latest.requested_daily_limit_cents / 100)} a day. We are
                reviewing it.</>
            )}
            {latest.status === "approved" && (
              <>Approved at {money((latest.granted_daily_limit_cents
                || latest.requested_daily_limit_cents) / 100)} a day.
                {latest.decision_note ? ` ${latest.decision_note}` : ""}</>
            )}
            {latest.status === "declined" && (
              <>Not approved. {latest.decision_note}</>
            )}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="limit-request-dialog">
          <DialogHeader>
            <DialogTitle className="display text-2xl tracking-tight">
              Request a higher limit
            </DialogTitle>
          </DialogHeader>
          <p className="-mt-1 text-sm text-foreground/55">
            We look at your connected bank, your Verqo trading history and your verification
            before deciding.
          </p>

          <Label className="mono mt-3 text-xs uppercase tracking-[0.18em] text-foreground/50">
            Daily limit you need
          </Label>
          <Input type="number" min="1" value={form.requested} className="mt-1.5"
                 placeholder="500000" data-testid="limit-request-amount"
                 onChange={(e) => setForm({ ...form, requested: e.target.value })} />

          <Label className="mono mt-3 text-xs uppercase tracking-[0.18em] text-foreground/50">
            Expected monthly volume (optional)
          </Label>
          <Input type="number" min="0" value={form.volume} className="mt-1.5"
                 placeholder="2000000" data-testid="limit-request-volume"
                 onChange={(e) => setForm({ ...form, volume: e.target.value })} />

          <Label className="mono mt-3 text-xs uppercase tracking-[0.18em] text-foreground/50">
            Why do you need it?
          </Label>
          <textarea
            value={form.reason} rows={3} data-testid="limit-request-reason"
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="Two large seasonal POs land in the same week and we pay our suppliers same day."
            className="mt-1.5 w-full rounded-md border border-foreground/15 bg-background px-3 py-2 text-sm"
          />

          <Button onClick={submit} disabled={busy} className="mt-4 h-11 rounded-full"
                  data-testid="limit-request-submit">
            Send request
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
